import type { ElementFileUploadEvent, SurveyElementJson } from '@coorum-research/shared-common/types'
import type { SurveyAnswerRecord } from '@coorum-research/shared-common/types/answer.type'
import type { CampaignLogicConfig } from '@coorum-research/shared-common/types/campaign.type'
import type { SurveyConfig } from '@coorum-research/shared-common/types/survey-json.type'
import type { ThemeConfig } from '@coorum-research/shared-common/types/theme.type'
import type { AfterRenderPageEvent, SurveyModel } from 'survey-core'
import { useDocumentVisibility } from '@react-hookz/web'
import { isString } from 'es-toolkit'
import { memo, useEffect, useMemo, useState } from 'react'
import { Survey } from 'survey-react-ui'
import { useOnceCall } from '../composables/use-once-call'
import { useSurveyModelPerQuestion } from '../composables/use-survey-model-per-question'
import { coorumColors } from '../survey/coorumTheme'
import { filterAnswerSkippedData } from '../survey/survey-answer-filter.util'
import { surveyLogic } from '../survey/survey-logic'
import { SurveyElementComment } from './survey-elements/SurveyElementComment'
import { SurveyElementDate } from './survey-elements/SurveyElementDate'
import { SurveyElementFile } from './survey-elements/SurveyElementFile'
import { SurveyElementImagepicker } from './survey-elements/SurveyElementImagepicker'
import { SurveyElementNumber } from './survey-elements/SurveyElementNumber'
import { SurveyElementPanel } from './survey-elements/SurveyElementPanel'
import { SurveyElementRating } from './survey-elements/SurveyElementRating'
import { SurveyElementText } from './survey-elements/SurveyElementText'
import {
  getElementByPageIndex,
  getElementValueByPageIndex,
  useSurveyElementForm,
} from './survey-elements/use-survey-element-form'
import { SurveyComponentFlowNavigation } from './SurveyComponentFlowNavigation'
import { SurveyComponentFlowProgress } from './SurveyComponentFlowProgress'
import { SwitchCase } from './SwitchCase'
import { ThemeContext } from './ThemeContext'

type Props = {
  surveyConfig: SurveyConfig | string | null
  themeJson?: ThemeConfig | null
  mode?: 'display' | 'edit' // TODO: survey-coreに型があるならそれを使う
  onAfterRenderPage?: (sender: SurveyModel, options?: AfterRenderPageEvent) => void
  onUploadFiles: (option: ElementFileUploadEvent) => Promise<void>
  campaignLogicJson: CampaignLogicConfig | null
  onViewQuestion?: (surveyElement?: SurveyElementJson) => void
  onCompleteQuestion?: (answerValue: SurveyAnswerRecord) => void
  onComplete: (nextLaunchContentId: string | null, senderData: SurveyAnswerRecord | null) => void
  getCurrentSurveyData?: () => SurveyAnswerRecord | null
  surveyCompleteDisabled?: boolean
  campaignDefaultLauncherContentId?: string | null
}

const MemoSurvey = memo(Survey)

export function SurveyComponentFlow({
  surveyConfig,
  getCurrentSurveyData,
  onViewQuestion,
  onCompleteQuestion,
  onComplete,
  themeJson,
  mode,
  onUploadFiles,
  campaignLogicJson,
  surveyCompleteDisabled,
  campaignDefaultLauncherContentId,
}: Props) {
  const {
    getActionJumpQuestionIndex,
    getActionJumpLauncherContentId,
    getNextLauncherContentId,
  } = surveyLogic()

  // 全設問設定データ
  const allElements = useMemo(() => {
    if (!surveyConfig) {
      return []
    }

    let parsed: SurveyConfig | null = null
    if (isString(surveyConfig)) {
      // TODO: SurveyWithConfigResponseへの切り替え完了後削除
      // apiを切り替えてなくSurveyResponseを使ってる部分のため、surveyConfigで、stringも受け入れる。(SurveyWithConfigResponseを推奨)
      try {
        parsed = JSON.parse(surveyConfig)
      } catch (e) {
        console.error(e)
      }
    } else {
      parsed = surveyConfig
    }

    if (!parsed) {
      return []
    }

    // 「表示」オプションが無効の設問を除外
    parsed.elements = parsed.elements?.filter(el => el.visible !== false)

    return parsed.elements ?? []
  }, [surveyConfig])

  const questionLogics = campaignLogicJson?.logics ?? []

  const [answerPageIndexStack, setAnswerPageIndexStack] = useState<number[]>([0])
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0)
  const [isUploading, setIsUploading] = useState(false)

  // 現在の設問の「設定データ(element)」を取得
  // - 全部ではなく、1質問1ページつづsurvey componentに渡す
  // - ページ遷移をsurveyjsに任せないで、自前で制御するため。
  const currentElementJson = useMemo(() => {
    if (allElements.length > 0) {
      return getElementByPageIndex(currentPageIndex, allElements)
    } else {
      return null
    }
  }, [allElements, currentPageIndex])

  // 現在の設問の「回答済みデータ」を取得
  const currentElementResultRecord = useMemo(() => {
    if (!currentElementJson) {
      return null
    }

    return getElementValueByPageIndex(
      currentElementJson,
      getCurrentSurveyData ? getCurrentSurveyData() : null,
    )
  }, [currentElementJson, getCurrentSurveyData])

  const { getModel } = useSurveyModelPerQuestion({
    themeJson,
    mode,
    onUploadFiles,
  })

  // currentElementの form state, validate, validation messageを管理する
  const {
    currentForm,
    validationError,
    setValidationError,
  } = useSurveyElementForm({
    currentElementJson,
    currentElementResultRecord,
    getModel,
  })

  // survey model.cssがdynamicにセットされるため、mobileのslideUp animationが終わった後にsurvey modelをrenderする
  const [showSurvey, setShowSurvey] = useState(false)
  const isDocumentVisible = useDocumentVisibility()
  useEffect(() => {
    if (isDocumentVisible) {
      setTimeout(() => {
        setShowSurvey(true)
      }, 100)
    }
  }, [isDocumentVisible])

  // データ計測（popupView)：最初の質問の記録
  useOnceCall(() => {
    if (onViewQuestion && currentElementJson) {
      onViewQuestion(currentElementJson)
    }
  })

  // back survey page
  const handleNavigationBack = () => {
    if (answerPageIndexStack.length > 0) {
      const newStack = [...answerPageIndexStack]
      newStack.pop()
      const previousPageIndex = newStack[newStack.length - 1]
      setAnswerPageIndexStack(newStack)
      setCurrentPageIndex(previousPageIndex)
      setValidationError('') // バリデーションエラーをクリア

      // データ計測（popupView)：前の設問の記録
      const previousElement = getElementByPageIndex(previousPageIndex, allElements)
      if (onViewQuestion && previousElement) {
        onViewQuestion(previousElement)
      }
    }
  }

  // next survey page
  const handleNavigationNext = () => {
    if (!currentForm || !currentElementJson) {
      return
    }

    // 手動でvalidationを実行 (SurveyModel)
    const { isValid, errorMessage } = currentForm.validate()
    if (!isValid) {
      if (errorMessage) {
        setValidationError(errorMessage)
      }
      return
    }

    setValidationError('') // バリデーションエラーがなければ、エラーメッセージをクリア

    if (onCompleteQuestion) {
      onCompleteQuestion(currentForm.data)
    }

    // calculate next page index
    const nextQuestionIndex = getActionJumpQuestionIndex(questionLogics, allElements, currentElementJson, currentForm.data)

    if (nextQuestionIndex === -1) {
      // 分岐ロジックでのサンクスページ
      const actionJumpLauncherContentId = getActionJumpLauncherContentId(questionLogics, currentElementJson, currentForm.data)
      // defaultサンクスページと分岐ロジックでのサンクスページのどちらかを選択
      const nextLaunchContentId = getNextLauncherContentId(actionJumpLauncherContentId, campaignDefaultLauncherContentId)

      const currentSurveyData = getCurrentSurveyData ? getCurrentSurveyData() : null
      const filteredSurveyData = filterAnswerSkippedData(currentSurveyData, allElements, answerPageIndexStack)
      onComplete(nextLaunchContentId, filteredSurveyData)
    } else {
      setCurrentPageIndex(nextQuestionIndex)
      setAnswerPageIndexStack([...answerPageIndexStack, nextQuestionIndex])

      // データ計測（popupView)：次の設問の記録
      const nextElement = getElementByPageIndex(nextQuestionIndex, allElements)
      if (onViewQuestion && nextElement) {
        onViewQuestion(nextElement)
      }
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0) // ページ最上部にスクロール
  }, [])

  const themeContextValue = useMemo(() => ({
    ...coorumColors,
    ...themeJson,
  }), [themeJson])

  // パネル要素用のファイルアップロードハンドラーは削除し、SurveyElementPanelに移行

  return (
    <ThemeContext value={themeContextValue}>
      {showSurvey && currentForm && (
        <>
          <div className="absolute top-0 h-[56px] w-full">
            <SurveyComponentFlowProgress
              currentPageIndex={currentPageIndex}
              allElements={allElements}
            />
          </div>
          <div className="absolute top-14 size-full h-[calc(100%-146px)] overflow-y-auto overflow-x-hidden">
            {currentForm && (
              <SwitchCase
                key={currentForm.elementJson.name}
                value={currentForm.elementJson.type as string}
                caseBy={{
                  imagepicker: () => (
                    <SurveyElementImagepicker
                      currentForm={currentForm}
                      validationError={validationError}
                      onValidationError={setValidationError}
                    />
                  ),
                  file: () => (
                    <SurveyElementFile
                      currentForm={currentForm}
                      validationError={validationError}
                      onValidationError={setValidationError}
                      onUploadFiles={onUploadFiles}
                      onUploadingChange={setIsUploading}
                    />
                  ),
                  comment: () => (
                    <SurveyElementComment
                      currentForm={currentForm}
                      validationError={validationError}
                    />
                  ),
                  text: () => (
                    <SurveyElementText
                      currentForm={currentForm}
                      validationError={validationError}
                    />
                  ),
                  number: () => (
                    <SurveyElementNumber
                      currentForm={currentForm}
                      validationError={validationError}
                    />
                  ),
                  date: () => (
                    <SurveyElementDate
                      currentForm={currentForm}
                      validationError={validationError}
                    />
                  ),
                  panel: () => (
                    <SurveyElementPanel
                      currentForm={currentForm}
                      validationError={validationError}
                      onUploadFiles={onUploadFiles}
                      onUploadingChange={setIsUploading}
                      onValidationError={setValidationError}
                    />
                  ),
                  rating: () => (
                    <SurveyElementRating
                      currentForm={currentForm}
                      validationError={validationError}
                    />
                  ),
                }}
                defaultComponent={() => (
                  <MemoSurvey model={currentForm.surveyModel} />
                )}
              />
            )}
          </div>
          <div className="absolute bottom-0 h-[90px] w-full">
            <SurveyComponentFlowNavigation
              currentPageIndex={currentPageIndex}
              allElements={allElements}
              onClickBack={handleNavigationBack}
              onClickNext={handleNavigationNext}
              disabled={surveyCompleteDisabled && isUploading}
            />
          </div>
        </>
      )}
    </ThemeContext>
  )
}
